import { useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";
import {
  BillingAddressCell,
  ShipmentCell,
  formatAmount,
  formatDate,
  formatPaymentStatus,
} from "./OrderManager.js";

const getItemName = (item) =>
  item.name || item.fragranceNote || item.scent || "Untitled";

const formatItemType = (itemType) =>
  itemType === "candle" ? "Candle" : "Product";

const BuildOrderItemsCell = ({ items = [], currency }) => {
  if (items.length === 0) return <small>No items saved</small>;

  return items.map((item) => (
    <span
      className="admin-build-order-item"
      key={`${item.itemType}-${item.id}`}
    >
      {getItemName(item)}
      <small>
        {formatItemType(item.itemType)} - Quantity: {item.quantity}
        {typeof item.totalAmount === "number" && currency
          ? ` - ${formatAmount(item.totalAmount, currency)}`
          : ""}
      </small>
    </span>
  ));
};

export default function BuildOrderManager({
  buildOrders = [],
  loadError = "",
  onChanged,
  onUnauthorized,
}) {
  const [deletingId, setDeletingId] = useState("");
  const [workingId, setWorkingId] = useState("");
  const [error, setError] = useState("");

  const removeBuildOrder = async (buildOrder) => {
    if (deletingId) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this build order?",
    );
    if (!confirmed) return;

    setDeletingId(buildOrder.id);
    setError("");
    try {
      await api.admin.buildOrders.remove(buildOrder.id, getAdminToken());
      await onChanged();
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      setError(requestError.message || "Could not delete the build order");
    } finally {
      setDeletingId("");
    }
  };

  const runShipmentAction = async (buildOrder, action) => {
    if (workingId) return;
    setWorkingId(buildOrder.id);
    setError("");
    try {
      await action();
      await onChanged();
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      setError(requestError.message || "Could not update the UPS shipment");
    } finally {
      setWorkingId("");
    }
  };

  const retryShipment = (buildOrder) =>
    runShipmentAction(buildOrder, () =>
      api.admin.buildOrders.retryShipment(buildOrder.id, getAdminToken()),
    );

  const markDroppedOff = (buildOrder) =>
    runShipmentAction(buildOrder, () =>
      api.admin.buildOrders.markDroppedOff(buildOrder.id, getAdminToken()),
    );

  const voidShipment = (buildOrder) => {
    const confirmed = window.confirm(
      "Void this UPS label? The order payment will not be refunded.",
    );
    if (!confirmed) return;
    return runShipmentAction(buildOrder, () =>
      api.admin.buildOrders.voidShipment(buildOrder.id, getAdminToken()),
    );
  };

  const downloadLabel = (buildOrder, labelIndex) =>
    runShipmentAction(buildOrder, () =>
      api.admin.buildOrders.downloadLabel(
        buildOrder.id,
        labelIndex,
        getAdminToken(),
      ),
    );

  return (
    <section className="admin-panel">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Stripe payments</p>
          <h2>Build orders</h2>
        </div>
      </div>

      {error && (
        <p className="admin-form-error" role="alert">
          {error}
        </p>
      )}
      {loadError && (
        <p className="admin-form-error" role="alert">
          {loadError}
        </p>
      )}

      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Address</th>
              <th>Items</th>
              <th>Total</th>
              <th>Payment Status</th>
              <th>UPS</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {buildOrders.map((buildOrder) => (
              <tr key={buildOrder.id}>
                <td>
                  <small>{buildOrder.id}</small>
                </td>
                <td>
                  {buildOrder.customer?.fullName || "Not provided"}
                  {buildOrder.customer?.email && (
                    <small>
                      <a href={`mailto:${buildOrder.customer.email}`}>
                        {buildOrder.customer.email}
                      </a>
                    </small>
                  )}
                  {buildOrder.customer?.phone && (
                    <small>{buildOrder.customer.phone}</small>
                  )}
                </td>
                <td>
                  <BillingAddressCell address={buildOrder.billingAddress} />
                </td>
                <td>
                  <BuildOrderItemsCell
                    items={buildOrder.items}
                    currency={buildOrder.currency}
                  />
                </td>
                <td>
                  {formatAmount(buildOrder.totalAmount, buildOrder.currency)}
                </td>
                <td>
                  <span
                    className={`admin-payment-status is-${buildOrder.paymentStatus}`}
                  >
                    {formatPaymentStatus(buildOrder.paymentStatus)}
                  </span>
                </td>
                <td>
                  <ShipmentCell
                    order={buildOrder}
                    actions={{ retry: "Retry label" }}
                    workingId={workingId}
                    onRetry={retryShipment}
                    onDownload={downloadLabel}
                    onDroppedOff={markDroppedOff}
                    onVoid={voidShipment}
                  />
                </td>
                <td>{formatDate(buildOrder.createdAt)}</td>
                <td>
                  <button
                    type="button"
                    className="admin-delete"
                    onClick={() => removeBuildOrder(buildOrder)}
                    disabled={Boolean(deletingId)}
                  >
                    {deletingId === buildOrder.id ? "Deleting..." : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
            {buildOrders.length === 0 && (
              <tr>
                <td colSpan="9">No build orders yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
